import React, { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Autocomplete, Box, Tab, TextField } from '@mui/material'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { isNil } from 'lodash'

import { useStateContext } from '../../../context/ContextProvider'
import axios from '../../../lib/axios'
import Loading from '../../../components/Loading'
import { handleAxiosError, isValid } from '../../../helpers/helper'
import { TEXTFIELD_STYLE } from '../../../constants/constants'

const ShowQuotePricingTier = () => {

    const { t } = useTranslation()
    const { config, pusher } = useStateContext()
    const params = useParams()
    const navigate = useNavigate()
    const location = useLocation()

    /* state */
    const [isLoading, setIsLoading] = useState(false)

    /* form */
    const [customerId, setCustomerId] = useState('')
    const [customerName, setCustomerName] = useState('')
    const [itemLabel, setItemLabel] = useState('')
    const [minQuantity, setMinQuantity] = useState('')
    const [maxQuantity, setMaxQuantity] = useState('')
    const [unitPrice, setUnitPrice] = useState('')
    const [effectiveFrom, setEffectiveFrom] = useState(null)
    const [effectiveTo, setEffectiveTo] = useState(null)

    const goBack = () => {
        navigate((!isNil(location.state) && !isNil(location.state.prevPathname)) ? location.state.prevPathname : '/quote-pricing-tiers')
    }

    useEffect(() => {
        function handleKeyDown(e) {
            if (e.keyCode == '27') {
                goBack()
            }
        }

        document.addEventListener('keydown', handleKeyDown);

        return function cleanup() {
            document.removeEventListener('keydown', handleKeyDown);
        }
    }, [location])

    useEffect(() => {
        getPricingTier()
    }, [params.id])

    /* real-time: refresh if another user updates this pricing tier, leave if they delete it */
    useEffect(() => {
        const clientId = localStorage.getItem('client_id')
        const channelUpdated = pusher.subscribe(`${clientId}-quotepricingtier-updated`)
        const channelDeleted = pusher.subscribe(`${clientId}-quotepricingtier-deleted`)

        channelUpdated.bind(`${clientId}-quotepricingtier-updated-event`, data => {
            if (Number(data.id) === Number(params.id)) {
                getPricingTier()
            }
        })

        channelDeleted.bind(`${clientId}-quotepricingtier-deleted-event`, data => {
            if (Number(data.id) === Number(params.id)) {
                goBack()
            }
        })

        return (() => {
            pusher.unsubscribe(`${clientId}-quotepricingtier-updated`)
            pusher.unsubscribe(`${clientId}-quotepricingtier-deleted`)
        })
    }, [])

    /* requests */

    const getPricingTier = async () => {
        setIsLoading(true)
        await axios.get(`/api/quote-pricing-tiers/${params.id}`, config)
            .then(res => {
                const tier = res.data?.data

                setCustomerId(tier?.customer_id ?? '')
                setCustomerName(tier?.customer_name ?? '')
                setItemLabel(tier?.item_no ?? '')
                setMinQuantity(isValid(tier?.min_quantity) ? String(tier.min_quantity) : '0')
                setMaxQuantity(isValid(tier?.max_quantity) ? String(tier.max_quantity) : '')
                setUnitPrice(isValid(tier?.unit_price) ? String(tier.unit_price) : '')
                setEffectiveFrom(tier?.effective_from ? dayjs(tier.effective_from) : null)
                setEffectiveTo(tier?.effective_to ? dayjs(tier.effective_to) : null)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <Box sx={{ background: '#F5F5FD', minHeight: '100vh' }}>
                <div className='w-full min-h-screen bg-white'>
                    <div className='flex border-b justify-between items-center p-5 w-full bg-white' style={{ borderRadius: '5px 5px 0 0' }}>
                        <div className='flex items-center justify-between w-full leading-none'>
                            <p className='text-[18px] font-[600] text-[#333333] flex items-center'><span className='text-[#336195] text-[35px] mr-4'>•</span> {t('view_pricing_tier')}</p>
                            <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-[#333333]">
                                <button onClick={goBack}><i className="fa-solid fa-plus"></i></button>
                            </div>
                        </div>
                    </div>

                    <TabContext value="1">
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <TabList aria-label="pricing tier tabs">
                                <Tab sx={{ textTransform: 'none' }} value="1" label={t('general')} icon={<i className="fa-solid fa-circle-info"></i>} iconPosition='start' />
                            </TabList>
                        </Box>
                        <TabPanel value="1">
                            <form className='flex justify-center flex-col items-start mt-2'>
                                {/* Customer */}
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('customer')}</p>
                                    <Autocomplete
                                        disabled
                                        disablePortal
                                        disableClearable
                                        id="customers-autocomplete"
                                        options={[]}
                                        value={{ label: customerName, id: customerId }}
                                        sx={{
                                            width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                            boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                        }}
                                        renderInput={(params) => <TextField {...params} />}
                                    />
                                </div>

                                {/* Item */}
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('item')}</p>
                                    <TextField
                                        disabled
                                        sx={TEXTFIELD_STYLE}
                                        value={itemLabel}
                                    />
                                </div>

                                {/* Minimum Quantity */}
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('minimum_quantity')}</p>
                                    <TextField
                                        disabled
                                        sx={TEXTFIELD_STYLE}
                                        type="text"
                                        value={minQuantity}
                                    />
                                </div>

                                {/* Maximum Quantity */}
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('maximum_quantity')}</p>
                                    <TextField
                                        disabled
                                        sx={TEXTFIELD_STYLE}
                                        type="text"
                                        value={maxQuantity}
                                    />
                                </div>

                                {/* Unit Price */}
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('unit_price')}</p>
                                    <TextField
                                        disabled
                                        sx={TEXTFIELD_STYLE}
                                        type="text"
                                        value={unitPrice}
                                    />
                                </div>

                                {/* Effective From */}
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('effective_from')}</p>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <MobileDatePicker
                                            disabled
                                            sx={TEXTFIELD_STYLE}
                                            format="YYYY-MM-DD"
                                            value={effectiveFrom}
                                        />
                                    </LocalizationProvider>
                                </div>

                                {/* Effective To */}
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('effective_to')}</p>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <MobileDatePicker
                                            disabled
                                            sx={TEXTFIELD_STYLE}
                                            format="YYYY-MM-DD"
                                            value={effectiveTo}
                                        />
                                    </LocalizationProvider>
                                </div>
                            </form>
                        </TabPanel>
                    </TabContext>

                    
                </div>
            </Box>
        </>
    )
}

export default ShowQuotePricingTier
